<template>
  <v-row no-gutters>
    <x-table
      :columns="columns"
      :loading="loading"
      :sortBy="sortUsers"
      :updateTableSize="updateTableRowCount"
      :tableRows="userList"
      :data-test-id="dataTestId"
      :paginationData="{
        pageSize: recordsPerPage,
        pageNumber: currentPage,
        totalRecords,
        setPage
      }"
    >
      <template slot="table-top-right">
        <v-text-field
          class="search-text pt-2 w-70"
          v-model="searchText"
          placeholder="Search"
          @input="searchUsers"
          :data-test-id="`${dataTestId}__search-box`"
        >
          <v-icon
            slot="append"
            :color="primaryColor"
            :data-test-id="`${dataTestId}__search-icon`"
            >search</v-icon
          >
        </v-text-field>
      </template>
      <template slot="table_item" slot-scope="props">
        <tr
          v-for="item in props.tableRows"
          :key="item.id"
          class="cursorPointer"
          @click="editUser(item.id)"
        >
          <td class="text-left">
            <span>{{ item.name }}</span>
          </td>
          <td class="text-left">
            <Tooltip v-slot:tooltiptext :text="`${item.emailAliases.join('\n')}`" :icontooltip="false">
              {{ item.primaryEmail }}
            </Tooltip>
          </td>
          <td class="text-left">
            <span>{{ item.status }}</span>
          </td>
          <td class="text-left word-break-all">
            <span>{{ item.role | removeUnderScores }}</span>
          </td>
          <td class="text-left">
            <span>{{ item.license }}</span>
          </td>
        </tr>
      </template>
    </x-table>
  </v-row>
</template>
<script>
const debounceTimeInMilliseconds = 800;
const debounce = require('lodash.debounce');
import { getTableColumns } from '@/helpers/TableHelpers';
import Tooltip from '@/components/Common/Tooltip';
import { mapState } from 'vuex';
export default {
  name: 'UserTable',
  components: {
    Tooltip
  },
  data() {
    return {
      columns: [],
      recordsPerPage: 20,
      totalRecords: 0,
      currentPage: 1,
      searchText: '',
      sortBy: 'firstName',
      sortOrder: 'ASC',
      userList: []
    };
  },
  computed: {
    ...mapState(['orgId']),
    primaryColor() {
      return this.$vuetify?.theme?.themes?.light?.primary;
    },
    canViewUsers() {
      return this.orgId === this.currentOrgId
        ? this.$can('view', 'user')
        : this.$can('view', 'child_organisation_user');
    }
  },
  mounted() {
    this.initColumns();
    this.getUsers();
  },
  methods: {
    initColumns() {
      this.columns = this.copyArray(getTableColumns('users'));
      this.columns[0].field = 'firstName';
    },
    updateTableRowCount(pageSize) {
      this.currentPage = 1;

      this.recordsPerPage = pageSize;
      this.getUsers();
    },
    sortUsers(tableSortData) {
      this.sortBy = tableSortData.sortBy;
      this.sortOrder = tableSortData.sortOrder;
      this.getUsers();
    },
    setPage(page) {
      this.currentPage = page;
      this.getUsers();
    },
    searchUsers: debounce(function() {
      this.pageSize = 20;
      this.currentPage = 1;
      this.getUsers();
    }, debounceTimeInMilliseconds),

    async getUsers() {
      this.$emit('toggleUserLoadingState', true);

      this.userList = [];
      if (!this.canViewUsers) {
        this.$emit('toggleUserLoadingState', false);
        // NO_PERMISSION
        this.snackbar('error', this.$message.NO_PERMISSION);
        return;
      }
      const userStatuses = this.userStatuses.join(',');

      if (!userStatuses) {
        this.userList = [];
        this.$emit('toggleUserLoadingState', false);
        this.totalRecords = 0;
        return;
      }
      try {
        const userList = await this.$services.user.loadUsersAll(
          false,
          false,
          null,
          {
            pageNumber: this.currentPage || 1,
            pageSize: this.recordsPerPage,
            userStatuses: userStatuses,
            sortBy: this.sortBy,
            sortOrder: this.sortOrder,
            searchText: this.searchText
          }
        );
        const userListData = this.parseResponse(userList);
        if (!userListData?.[0]) {
          this.$emit('toggleUserLoadingState', false);
          return false;
        }

        this.totalRecords = userListData?.[0]?.metaData?.totalResults;
        this.formatUserList(userListData?.[0]?.results || []);
        this.$emit('toggleUserLoadingState', false);
      } catch (err) {
        this.snackbar('error', err);
        this.$emit('toggleUserLoadingState', false);
        this.userList = [];
      }
    },
    formatUserList(users) {
      this.userList = users.map(userDetail => {
        const emailAliases = userDetail?.email.slice(1);
        return {
          id: userDetail?.id || '',
          name: this.formatFullName(userDetail),
          primaryEmail: userDetail?.email?.[0] || '',
          emailAliases: emailAliases.length
            ? emailAliases
            : ['No aliases present'],
          email: this.formatEmailList(userDetail.email),
          status: userDetail?.status || '',
          role: userDetail?.roles ? userDetail.roles.join(', ') : 'No Role',
          license: userDetail?.license
            ? userDetail.license?.[0].toUpperCase() +
              userDetail.license.slice(1)
            : 'No License'
        };
      });
    },
    formatEmailList(emails) {
      const primaryEmail = emails?.[0] || '';
      const emailAliases =
        emails.length > 1 ? emails.slice(1).join('<br>') : 'No aliases present';
      const emailHTML = `<div class="tooltip">
            ${primaryEmail}
            <span class="tooltiptext">
              ${emailAliases}
            </span>
          </div>`;
      return emailHTML;
    }
  },
  props: {
    editUser: Function,
    userStatuses: Array,
    reloadUsers: Boolean,
    currentOrgId: String,
    getCount: Function,
    loading: Boolean,
    dataTestId: String
  },

  watch: {
    currentOrgId() {
      this.currentPage = 1;
      this.userList = [];
      this.getUsers();
      this.getCount();
    },
    reloadUsers() {
      this.getUsers();
    }
  }
};
</script>
